import { flow, getEnv, types } from "mobx-state-tree";

import {
  DataGridResponseWithDynamicProperties,
  HeaderItemResponse,
  RowResponse,
} from "../../api-models/response/DataGridResponse";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { sortColumns } from "../../components/datatable/helpers/sortExtensions";
import { moveItemInArray } from "../../helpers/ArrayHelpers";
import { endpoints } from "../api/ApiEndpoints";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { PROFIL_BREITE_CODE, HOHE_LANGE_CODE } from "../../api-models/response/BluemetricFields";
import { SewerObject, getTranslationKeyForType } from "./SewerObject";
import { SewerObjectType } from "../../generated/SewerObjects";

const esicTableColumns: Column[] = [
  {
    key: "Name",
    name: "general.sewer-objects.list-header.sewer-object-id",
    type: ColumnType.String,
    sortable: true,
    filterable: true,
    containsLink: true,
  },
  {
    key: "Type",
    name: "general.type",
    type: ColumnType.SingleSelect,
    valueOptions: [
      {
        label: getTranslationKeyForType(SewerObjectType.Section),
        value: SewerObjectType.Section.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Node),
        value: SewerObjectType.Node.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Structure),
        value: SewerObjectType.Structure.toString(),
      },
    ],
    sortable: true,
    filterable: true,
  },
  {
    key: "InspectionsCount",
    name: "general.inspections",
    type: ColumnType.Number,
    sortable: true,
    filterable: true,
  },
  {
    key: "Modified",
    name: "general.last-edit",
    type: ColumnType.DateTime,
    sortable: true,
    filterable: true,
  },
];

export const ProjectSewerObjectsStorageModel = types
  .compose(
    types.model({
      projectId: types.number,
      isDeletingSewerObjects: false,
    }),
    DataGridStorageModel
  )
  .named("ProjectSewerObjectsStorageModel")
  .actions((self) => {
    const getHeaders = (response: DataGridResponseWithDynamicProperties) => {
      const sewerObjectsHeaders = response.header as HeaderItemResponse[];
      if (!sewerObjectsHeaders.length) return esicTableColumns;

      sewerObjectsHeaders.sort(sortColumns);
      const filteredHeaders = sewerObjectsHeaders.filter(
        (header) => header.key !== "Organization" && header.key !== "Project"
      );

      const BMColumns = filteredHeaders
        .filter((header) => esicTableColumns.every((x) => x.key !== header.key))
        .map((column: HeaderItemResponse) => {
          return {
            key: column.key,
            name: column.name,
            type: self.getColumnType(column.metadata),
            valueOptions: self.getColumnPredefinedValueOptions(column.metadata),
            sortable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
            filterable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
          };
        });
      const allColumns = esicTableColumns.concat(BMColumns);

      const modifiedColumnIndex = allColumns.findIndex((x) => x.key === "Modified");
      moveItemInArray(allColumns, modifiedColumnIndex, filteredHeaders.length - 1);

      return allColumns;
    };

    return {
      afterCreate() {
        self.defaultSort = { field: "Name", sort: "asc" };
      },
      mapItem(item: RowResponse) {
        return SewerObject._constructorFromRowItem(item, `/projects/${self.projectId}`);
      },
      mapResponse(response: DataGridResponseWithDynamicProperties) {
        self.data = {
          totalCount: response.totalCount,
          items: response.rows.map(self.mapItem),
        };
        self.gridColumns = getHeaders(response);
      },
      setProjectId(projectId: number) {
        self.projectId = projectId;
      },
      setIsDeletingSewerObjects(value: boolean) {
        self.isDeletingSewerObjects = value;
      },
      deleteSewerObjects: flow(function* (sewerObjectsIds: number[]) {
        const endpoint = endpoints.get("sewer_objects");
        yield getEnv(self).api.delete(endpoint, sewerObjectsIds);
        self.deselectAll();
        yield self.load();
      }),
    };
  })

  .views((self) => ({
    get isLoading() {
      return (!self.data && !self.error) || self.isDeletingSewerObjects;
    },
    get endpoint() {
      const endpoint = endpoints.get("project_sewer_objects_all");
      endpoint.routeParams = [self.projectId];
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get canLoadItems() {
      return !!self.projectId;
    },
    get hasSewerObjectsAfterDelete() {
      return self.data.totalCount - self.selectedItemsCount > 0;
    },
    get endpointOnlyIds() {
      const endpoint = endpoints.get("project_sewer_objects_ids_all");
      endpoint.routeParams = [self.projectId];
      return endpoint;
    },
  }));
